import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, EmptyState, Modal } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Lock, LockOpen, Receipt } from "lucide-react";

const AdminPanelPeriodsPage = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [yearFilter, setYearFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [confirmFor, setConfirmFor] = useState(null);
  const [acting, setActing] = useState(false);

  const years = useMemo(() => {
    const set = new Set(data.map((p) => p.year).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [data]);

  const load = async () => {
    try {
      const rows = await api.accounting.periods({ limit: 500 });
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await load();
      setLoaded(true);
    })();
  }, []);

  const filtered = useMemo(() => {
    return data
      .filter((p) => {
        if (yearFilter !== "all" && String(p.year) !== yearFilter) return false;
        if (stateFilter !== "all" && p.state !== stateFilter) return false;
        return true;
      })
      .sort((a, b) => (String(b.code) || String(b.id)).localeCompare(String(a.code) || String(a.id)));
  }, [data, yearFilter, stateFilter]);

  const stats = useMemo(() => {
    const open = data.filter((p) => p.state === "open");
    const closed = data.filter((p) => p.state === "closed");
    const moves = data.reduce((s, p) => s + (Number(p.move_count) || 0), 0);
    return { open: open.length, closed: closed.length, moves };
  }, [data]);

  const confirmAction = async () => {
    if (!confirmFor) return;
    setActing(true);
    try {
      if (confirmFor.action === "close") await api.accounting.closePeriod(confirmFor.id);
      else await api.accounting.openPeriod(confirmFor.id);
      toast(`Period ${confirmFor.action === "close" ? "closed" : "opened"}`, "success");
      setConfirmFor(null);
      await load();
    } catch (e) {
      toast(e.message || "Action failed", "error");
    }
    setActing(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader title="Account Periods" subtitle="Open and close accounting periods" />

      <div className="ess-sg ess-sg3" style={{ marginBottom: "24px" }}>
        <StatCard label="Open Periods" value={String(stats.open)} icon={LockOpen} color="green" trend="Locked for posting" />
        <StatCard label="Closed Periods" value={String(stats.closed)} icon={Lock} color="slate" trend="Posting blocked" />
        <StatCard label="Journal Entries" value={String(stats.moves)} icon={Receipt} color="indigo" trend="Across all periods" />
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} style={{ ...inputStyle, minWidth: "160px", cursor: "pointer" }}>
          <option value="all">All Years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ ...inputStyle, minWidth: "140px", cursor: "pointer" }}>
          <option value="all">All States</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {filtered.length === 0 ? (
          <EmptyState icon="🗓️" title="No periods" subtitle="Periods appear once configured in the accounting module" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ minWidth: "130px" }}>Period</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Code</th>
                  <th className="ess-th" style={{ minWidth: "150px" }}>From</th>
                  <th className="ess-th" style={{ minWidth: "150px" }}>To</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Moves</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>State</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => (
                  <tr key={row.id} className="ess-tr">
                    <td className="ess-td" style={{ fontWeight: "600", whiteSpace: "nowrap" }}>{row.name || "—"}</td>
                    <td className="ess-td" style={{ color: "#225f2b", fontFamily: "monospace", fontWeight: "600", whiteSpace: "nowrap" }}>{row.code || row.id}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(row.date_start)}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(row.date_end)}</td>
                    <td className="ess-td" style={{ fontWeight: "600", color: "#475569", textAlign: "right", whiteSpace: "nowrap" }}>{row.move_count ?? 0}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={{ background: row.state === "open" ? "#f0f7f0" : "#f1f5f9", color: row.state === "open" ? "#225f2b" : "#475569", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" }}>
                        {row.state === "open" ? "Open" : "Closed"}
                      </span>
                    </td>
                    <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {row.state === "open" ? (
                        <button onClick={() => setConfirmFor({ ...row, action: "close" })} className="ess-btn ess-btn-secondary" style={{ color: "#b45309", padding: "4px 10px", fontSize: "12px", fontWeight: "600" }}>Close</button>
                      ) : (
                        <button onClick={() => setConfirmFor({ ...row, action: "open" })} className="ess-btn ess-btn-secondary" style={{ color: "#225f2b", padding: "4px 10px", fontSize: "12px", fontWeight: "600" }}>Reopen</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={!!confirmFor} onClose={() => setConfirmFor(null)} title={confirmFor ? `${confirmFor.action === "close" ? "Close" : "Reopen"} Period` : ""}>
        {confirmFor && (
          <div>
            <p style={{ fontSize: "14px", color: "#475569", marginBottom: "20px" }}>
              {confirmFor.action === "close"
                ? <>Close <strong>{confirmFor.name}</strong>? New journal entries in this period will be blocked. Posting is rejected until the period is reopened.</>
                : <>Reopen <strong>{confirmFor.name}</strong>? Journal entries will be able to be posted again in this period.</>}
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
              <button onClick={() => setConfirmFor(null)} className="ess-btn ess-btn-secondary">Cancel</button>
              <button onClick={confirmAction} disabled={acting} className="ess-btn ess-btn-primary">
                {acting ? "Saving…" : confirmFor.action === "close" ? "Close Period" : "Reopen Period"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPanelPeriodsPage;